import React, { useCallback, useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, TrendingUp, TrendingDown, Scale, PiggyBank, Download, Wallet, ArrowRightLeft, Lightbulb } from "lucide-react";
import { TaxTipToggle, TaxTipRows } from "@/components/simple-ledger/TaxTips";
import { toast } from "sonner";
import { apiClient } from "@/lib/apiClient";
import { useCompany } from "@/context/CompanyContext";
import { SimpleModeThresholdBanner, useAccountingModeSwitch } from "@/components/SimpleModeBanner";

const chf = (v) => `CHF ${Number(v || 0).toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function Kpi({ icon: Icon, label, value, sub, testid, tone = "" }) {
  return (
    <Card className={`p-4 border border-slate-200 bg-white ${tone}`} data-testid={testid}>
      <div className="flex items-center gap-2 text-xs text-slate-500"><Icon className="h-4 w-4" />{label}</div>
      <div className="mt-1 text-xl font-semibold text-slate-900 tabular-nums">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-0.5">{sub}</div>}
    </Card>
  );
}

function MonthTable({ months, totals, tips, tipsLoading, companyId, onBooked }) {
  const [openMonths, setOpenMonths] = useState(() => new Set());
  const toggle = (m) => setOpenMonths((prev) => { const n = new Set(prev); n.has(m) ? n.delete(m) : n.add(m); return n; });
  const max = Math.max(1, ...months.map((m) => Math.max(m.income, m.expenses)));
  return (
    <Card className="p-4 border border-slate-200 bg-white" data-testid="simple-ledger-months">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-medium text-slate-900">Einnahmen und Ausgaben pro Monat</h2>
        <span className="text-[11px] text-slate-500 inline-flex items-center gap-1" data-testid="simple-ledger-tips-status">
          <Lightbulb className="h-3 w-3" />{tipsLoading ? "Steuer-Tipps werden geprüft …" : tips ? `${tips.total} Steuer-Tipps${tips.ai_used ? " · mit KI-Erklärung" : ""}` : "Steuer-Tipps nicht verfügbar"}
        </span>
      </div>
      <table className="w-full text-sm">
        <thead><tr className="text-xs text-slate-500 border-b border-slate-200"><th className="text-left py-1.5 font-medium">Monat</th><th className="text-left py-1.5 font-medium w-1/3">Verlauf</th><th className="text-right py-1.5 font-medium">Einnahmen</th><th className="text-right py-1.5 font-medium">Ausgaben</th><th className="text-right py-1.5 font-medium">Ergebnis</th><th className="text-right py-1.5 font-medium">Steuer-Tipps</th></tr></thead>
        <tbody>
          {months.map((m) => {
            const mt = tips?.months?.[String(m.month)] || [];
            const open = openMonths.has(m.month);
            return (
              <React.Fragment key={m.month}>
                <tr className={`border-b border-slate-100 last:border-0 ${open ? "bg-slate-50" : ""}`} data-testid={`simple-ledger-month-${m.month}`}>
                  <td className="py-1.5 text-slate-700">{m.label}</td>
                  <td className="py-1.5 pr-3">
                    <div className="h-1.5 rounded bg-emerald-400" style={{ width: `${(m.income / max) * 100}%` }} />
                    <div className="h-1.5 rounded bg-rose-300 mt-0.5" style={{ width: `${(m.expenses / max) * 100}%` }} />
                  </td>
                  <td className="py-1.5 text-right tabular-nums text-emerald-700">{chf(m.income)}</td>
                  <td className="py-1.5 text-right tabular-nums text-rose-700">{chf(m.expenses)}</td>
                  <td className={`py-1.5 text-right tabular-nums font-medium ${m.result < 0 ? "text-rose-700" : "text-slate-900"}`}>{chf(m.result)}</td>
                  <td className="py-1.5 text-right"><TaxTipToggle tips={mt} open={open} onToggle={() => toggle(m.month)} month={m.month} /></td>
                </tr>
                {open && mt.length > 0 && <tr className="border-b border-slate-100"><td colSpan={6}><TaxTipRows tips={mt} month={m.month} companyId={companyId} onBooked={onBooked} /></td></tr>}
              </React.Fragment>
            );
          })}
        </tbody>
        <tfoot><tr className="border-t-2 border-slate-300 font-semibold"><td className="py-2">Total {""}</td><td /><td className="py-2 text-right tabular-nums text-emerald-700" data-testid="simple-ledger-total-income">{chf(totals.income)}</td><td className="py-2 text-right tabular-nums text-rose-700" data-testid="simple-ledger-total-expenses">{chf(totals.expenses)}</td><td className={`py-2 text-right tabular-nums ${totals.result < 0 ? "text-rose-700" : "text-slate-900"}`} data-testid="simple-ledger-total-result">{chf(totals.result)}</td><td /></tr></tfoot>
      </table>
    </Card>
  );
}

function CategoryList({ title, items, tone, testid }) {
  if (!items.length) return null;
  const total = items.reduce((s, c) => s + c.amount, 0) || 1;
  return (
    <Card className="p-4 border border-slate-200 bg-white" data-testid={testid}>
      <h2 className="text-sm font-medium text-slate-900 mb-3">{title}</h2>
      <ul className="space-y-1.5">
        {items.map((c) => (
          <li key={c.code} className="text-sm">
            <div className="flex justify-between gap-2"><span className="text-slate-700 truncate">{c.name}</span><span className="tabular-nums text-slate-900">{chf(c.amount)}</span></div>
            <div className="h-1 rounded bg-slate-100 mt-0.5"><div className={`h-1 rounded ${tone}`} style={{ width: `${Math.max(2, (c.amount / total) * 100)}%` }} /></div>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function NetWorth({ assets, liabilities, nw }) {
  const Group = ({ g }) => (
    <li className="py-1.5 border-b border-slate-100 last:border-0">
      <div className="flex justify-between text-sm font-medium text-slate-800"><span>{g.label}</span><span className="tabular-nums">{chf(g.amount)}</span></div>
      {g.accounts.map((a) => <div key={a.code} className="flex justify-between text-xs text-slate-500 pl-3"><span>{a.code} {a.name}</span><span className="tabular-nums">{chf(a.amount)}</span></div>)}
    </li>
  );
  return (
    <Card className="p-4 border border-slate-200 bg-white" data-testid="simple-ledger-networth">
      <h2 className="text-sm font-medium text-slate-900 mb-1">Vermögensübersicht</h2>
      <p className="text-xs text-slate-500 mb-3">Stand per {new Date(nw.as_of).toLocaleDateString("de-CH")}</p>
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-emerald-700 mb-1">Vermögen</div>
          <ul>{assets.length ? assets.map((g) => <Group key={g.label} g={g} />) : <li className="text-xs text-slate-400 italic">keine Bestände</li>}</ul>
          <div className="flex justify-between text-sm font-semibold mt-2 pt-2 border-t border-slate-300"><span>Total Vermögen</span><span className="tabular-nums" data-testid="simple-ledger-total-assets">{chf(nw.total_assets)}</span></div>
        </div>
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-rose-700 mb-1">Schulden</div>
          <ul>{liabilities.length ? liabilities.map((g) => <Group key={g.label} g={g} />) : <li className="text-xs text-slate-400 italic">keine Schulden</li>}</ul>
          <div className="flex justify-between text-sm font-semibold mt-2 pt-2 border-t border-slate-300"><span>Total Schulden</span><span className="tabular-nums" data-testid="simple-ledger-total-liabilities">{chf(nw.total_liabilities)}</span></div>
        </div>
      </div>
      <div className="mt-4 rounded-lg bg-slate-900 text-white px-4 py-3 flex justify-between items-center"><span className="text-sm">Reinvermögen (Vermögen − Schulden)</span><span className="text-lg font-semibold tabular-nums" data-testid="simple-ledger-net">{chf(nw.net)}</span></div>
    </Card>
  );
}

export default function SimpleLedger() {
  const { company } = useCompany();
  const companyId = company?._id;
  const { switchMode, busy, mode } = useAccountingModeSwitch();
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [tips, setTips] = useState(null);
  const [tipsLoading, setTipsLoading] = useState(false);

  const load = useCallback(() => {
    if (!companyId) return;
    setLoading(true);
    apiClient.get(`/companies/${companyId}/simple-ledger/summary`, { params: { year } }).then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
    setTips(null);
    setTipsLoading(true);
    apiClient.get(`/companies/${companyId}/simple-ledger/tax-tips`, { params: { year } }).then((r) => setTips(r.data)).catch(() => setTips(null)).finally(() => setTipsLoading(false));
  }, [companyId, year]);
  useEffect(load, [load]);

  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      const r = await apiClient.get(`/companies/${companyId}/simple-ledger/pdf`, { params: { year }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = Object.assign(document.createElement("a"), { href: url, download: `Einnahmen-Ausgaben-${year}.pdf` });
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("PDF konnte nicht erstellt werden"); }
    finally { setPdfBusy(false); }
  };

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);
  const t = data?.totals;
  return (
    <div className="space-y-6" data-testid="simple-ledger-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Einnahmen &amp; Ausgaben</h1>
          <p className="text-sm text-slate-500">Einfache Buchhaltung («Milchbüchlein», OR 957 Abs. 2): Einnahmen, Ausgaben und Vermögenslage – ohne Bilanz-Fachbegriffe. Im Hintergrund wird alles korrekt doppelt verbucht.</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={year} onChange={(e) => setYear(Number(e.target.value))} className="h-9 rounded-md border border-slate-200 px-2 text-sm" data-testid="simple-ledger-year">
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <Button size="sm" variant="outline" onClick={downloadPdf} disabled={pdfBusy || !data} data-testid="simple-ledger-pdf">
            {pdfBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4 mr-1" />}PDF für Steuererklärung
          </Button>
          {mode === "simple" ? (
            <Button size="sm" variant="ghost" className="text-slate-600" disabled={busy} onClick={() => switchMode("double")} data-testid="simple-ledger-switch-double"><ArrowRightLeft className="h-4 w-4 mr-1" />Zur doppelten Buchhaltung</Button>
          ) : (
            <Button size="sm" variant="ghost" className="text-slate-600" disabled={busy} onClick={() => switchMode("simple")} data-testid="simple-ledger-switch-simple"><ArrowRightLeft className="h-4 w-4 mr-1" />Milchbüchlein-Modus aktivieren</Button>
          )}
        </div>
      </div>

      {data && <SimpleModeThresholdBanner threshold={{ ...data.threshold, year: data.year }} />}

      {loading && !data ? <div className="flex items-center gap-2 text-slate-500 text-sm"><Loader2 className="h-4 w-4 animate-spin" />Lade …</div> : data && (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Kpi icon={TrendingUp} label={`Einnahmen ${data.year}`} value={chf(t.income)} testid="simple-ledger-kpi-income" />
            <Kpi icon={TrendingDown} label={`Ausgaben ${data.year}`} value={chf(t.expenses)} testid="simple-ledger-kpi-expenses" />
            <Kpi icon={Scale} label="Ergebnis (Gewinn / Verlust)" value={chf(t.result)} tone={t.result < 0 ? "border-rose-200" : "border-emerald-200"} testid="simple-ledger-kpi-result" />
            <Kpi icon={PiggyBank} label="Netto-Privatentnahmen" value={chf(t.private_net)} sub={`Entnahmen ${chf(t.private_withdrawals)} · Einlagen ${chf(t.private_deposits)}`} testid="simple-ledger-kpi-private" />
          </div>
          <MonthTable months={data.months} totals={t} tips={tips} tipsLoading={tipsLoading} companyId={companyId} onBooked={() => apiClient.get(`/companies/${companyId}/simple-ledger/summary`, { params: { year } }).then((r) => setData(r.data)).catch(() => {})} />
          <div className="grid gap-4 lg:grid-cols-2">
            <CategoryList title="Einnahmen nach Kategorie" items={data.income_categories} tone="bg-emerald-400" testid="simple-ledger-income-categories" />
            <CategoryList title="Ausgaben nach Kategorie" items={data.expense_categories} tone="bg-rose-300" testid="simple-ledger-expense-categories" />
          </div>
          <NetWorth assets={data.assets} liabilities={data.liabilities} nw={data.net_worth} />
          <Card className="p-4 border border-slate-200 bg-slate-50 text-xs text-slate-600 flex gap-2" data-testid="simple-ledger-hint">
            <Wallet className="h-4 w-4 shrink-0 text-slate-400" />
            <span>Privatentnahmen und -einlagen werden über das Privatkonto (285x oder «Privat») erfasst. Ab CHF 500'000 Jahresumsatz ist die doppelte Buchhaltung Pflicht – die Umstellung ist jederzeit ohne Datenverlust möglich.</span>
          </Card>
        </>
      )}
    </div>
  );
}
